import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Database, LineChart, PieChart, TrendingUp, Eye, Clock, Zap } from 'lucide-react';

const AnalyticsPage = () => {
  const analyticsServices = [
    {
      title: "Custom Dashboard Development",
      icon: <BarChart3 className="w-8 h-8" />,
      description: "Tailored analytics dashboards that provide real-time insights into your most important metrics and KPIs.",
      features: [
        "Real-time data visualization",
        "Custom metric calculations",
        "Multi-channel data integration",
        "Automated alert systems",
        "Mobile-responsive design",
        "Role-based access control"
      ],
      benefits: [
        "Single source of truth for all metrics",
        "Faster decision-making",
        "Improved data accessibility",
        "Automated reporting processes"
      ]
    },
    {
      title: "Advanced Attribution Modeling",
      icon: <TrendingUp className="w-8 h-8" />,
      description: "Sophisticated attribution models that reveal the true impact of each marketing touchpoint on conversions.",
      features: [
        "Multi-touch attribution analysis",
        "Cross-device customer journey mapping",
        "Media mix modeling",
        "Incrementality testing",
        "Customer lifetime value tracking",
        "Return on ad spend optimization"
      ],
      benefits: [
        "Better budget allocation decisions",
        "Improved campaign ROI",
        "Enhanced customer understanding",
        "Data-driven strategy development"
      ]
    },
    {
      title: "ETL & Data Pipeline Management",
      icon: <Database className="w-8 h-8" />,
      description: "Robust data infrastructure that ensures clean, accurate, and timely data flow from all sources.",
      features: [
        "Automated data extraction and transformation",
        "Data quality monitoring and cleansing",
        "Real-time and batch processing",
        "API integration management",
        "Data warehouse setup and maintenance",
        "Backup and disaster recovery"
      ],
      benefits: [
        "Reliable data infrastructure",
        "Reduced manual data work",
        "Improved data accuracy",
        "Scalable analytics foundation"
      ]
    }
  ];

  const dataSourceTypes = [
    {
      category: "Advertising Platforms",
      sources: ["Google Ads", "Facebook Ads", "Amazon Advertising", "Microsoft Ads", "TikTok Ads", "LinkedIn Ads"],
      icon: <Eye className="w-6 h-6" />
    },
    {
      category: "Analytics Platforms",
      sources: ["Google Analytics", "Adobe Analytics", "Mixpanel", "Amplitude", "Hotjar", "Crazy Egg"],
      icon: <LineChart className="w-6 h-6" />
    },
    {
      category: "E-commerce Platforms",
      sources: ["Shopify", "WooCommerce", "Magento", "BigCommerce", "Amazon Seller Central", "eBay"],
      icon: <PieChart className="w-6 h-6" />
    },
    {
      category: "CRM & Sales",
      sources: ["Salesforce", "HubSpot", "Pipedrive", "Zendesk", "Intercom", "Stripe"],
      icon: <Database className="w-6 h-6" />
    }
  ];

  const reportingCapabilities = [
    {
      type: "Executive Dashboards",
      description: "High-level KPI tracking for leadership teams with focus on business impact and ROI.",
      features: ["Revenue attribution", "ROAS by channel", "Customer acquisition costs", "Lifetime value metrics"],
      frequency: "Real-time updates"
    },
    {
      type: "Campaign Performance Reports",
      description: "Detailed campaign analytics with optimization recommendations and competitive insights.",
      features: ["Campaign comparison analysis", "Audience performance", "Creative performance", "Bid optimization suggestions"],
      frequency: "Daily and weekly"
    },
    {
      type: "Customer Journey Analytics",
      description: "Complete view of customer interactions across all touchpoints and channels.",
      features: ["Path to conversion analysis", "Funnel optimization", "Churn prediction", "Segmentation insights"],
      frequency: "Monthly deep-dives"
    },
    {
      type: "Predictive Analytics",
      description: "Forward-looking insights using machine learning to predict trends and opportunities.",
      features: ["Demand forecasting", "Budget optimization", "Customer behavior prediction", "Market trend analysis"],
      frequency: "Quarterly projections"
    }
  ];

  const analyticsTools = [
    {
      name: "Google Analytics 4",
      specialties: ["Enhanced e-commerce tracking", "Custom event analysis", "Audience building", "Conversion path analysis"]
    },
    {
      name: "Looker Studio",
      specialties: ["Custom dashboard creation", "Automated reporting", "Multi-source data blending", "Collaborative analytics"]
    },
    {
      name: "Tableau",
      specialties: ["Advanced data visualization", "Statistical analysis", "Predictive modeling", "Enterprise reporting"]
    },
    {
      name: "Power BI", 
      specialties: ["Business intelligence dashboards", "Real-time analytics", "AI-powered insights", "Microsoft ecosystem integration"]
    },
    {
      name: "Python & R",
      specialties: ["Statistical modeling", "Machine learning", "Custom analytics solutions", "Data science workflows"]
    },
    {
      name: "BigQuery",
      specialties: ["Large-scale data processing", "SQL analytics", "Machine learning integration", "Data warehouse management"]
    }
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="container">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="display-md text-[var(--text-primary)] mb-6">
            Advanced Analytics & Data Intelligence
          </h1>
          <p className="body-lg mb-12">
            Transform raw data into actionable insights with custom analytics solutions, 
            advanced attribution modeling, and automated reporting systems that drive better business decisions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary">
              Get Analytics Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/ops/pricing" className="btn-secondary">
              View Analytics Packages
            </Link>
          </div>
        </div>

        {/* Core Analytics Services */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Comprehensive Analytics Services
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              From custom dashboard development to advanced attribution modeling, 
              we provide the analytics infrastructure your business needs to thrive.
            </p>
          </div>

          <div className="space-y-12">
            {analyticsServices.map((service, index) => (
              <div key={service.title} className={`feature-card animate-fade-in`} style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-1">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-[var(--accent-bg)] rounded-2xl flex items-center justify-center mr-6">
                        <div className="text-[var(--accent-primary)]">{service.icon}</div>
                      </div>
                      <h3 className="h1 text-[var(--text-primary)]">{service.title}</h3>
                    </div>
                    <p className="body-lg mb-8">{service.description}</p>
                    
                    <div>
                      <h4 className="font-semibold text-[var(--text-primary)] mb-4">Key Benefits</h4>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center text-[var(--text-secondary)]">
                            <Zap className="w-4 h-4 text-[var(--accent-primary)] mr-3 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <h4 className="h3 text-[var(--text-primary)] mb-6">Service Features</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-4 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)]">
                          <div className="w-2 h-2 bg-[var(--accent-primary)] rounded-full flex-shrink-0 mt-2"></div>
                          <span className="text-[var(--text-secondary)]">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Data Source Integration */}
        <section className="mb-24 bg-[var(--bg-secondary)] -mx-6 px-6 py-16 rounded-2xl">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Comprehensive Data Integration
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Connect and unify data from all your marketing, sales, and business platforms 
              for a complete view of your performance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {dataSourceTypes.map((sourceType, index) => (
              <div key={sourceType.category} className={`feature-card text-center hover-lift animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <div className="text-[var(--accent-primary)]">{sourceType.icon}</div>
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{sourceType.category}</h3>
                
                <ul className="space-y-2">
                  {sourceType.sources.map((source, idx) => (
                    <li key={idx} className="text-sm text-[var(--text-secondary)]">
                      {source}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Reporting Capabilities */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Reporting & Visualization Capabilities
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              From executive dashboards to detailed campaign analysis, 
              get the insights you need at every level of your organization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {reportingCapabilities.map((report, index) => (
              <div key={report.type} className={`feature-card hover-lift animate-fade-in`} style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="flex items-start justify-between mb-6">
                  <h3 className="h2 text-[var(--text-primary)]">{report.type}</h3>
                  <span className="px-3 py-1 bg-[var(--accent-bg)] text-[var(--accent-primary)] rounded-full text-sm font-medium">
                    {report.frequency}
                  </span>
                </div>
                
                <p className="body-md mb-6">{report.description}</p>
                
                <div>
                  <h4 className="font-semibold text-[var(--text-primary)] mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {report.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-[var(--text-secondary)]">
                        <Clock className="w-4 h-4 text-[var(--accent-primary)] mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Analytics Tools & Technology */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Advanced Analytics Technology Stack
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              We leverage industry-leading analytics tools and technologies to deliver 
              enterprise-grade insights and reporting capabilities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {analyticsTools.map((tool, index) => (
              <div key={tool.name} className={`feature-card hover-lift animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <h3 className="h2 text-[var(--text-primary)] mb-6">{tool.name}</h3>
                
                <div>
                  <h4 className="font-semibold text-[var(--text-primary)] mb-3">Specialties</h4>
                  <ul className="space-y-2">
                    {tool.specialties.map((specialty, idx) => (
                      <li key={idx} className="flex items-center text-[var(--text-secondary)] text-sm">
                        <div className="w-2 h-2 bg-[var(--accent-primary)] rounded-full mr-3 flex-shrink-0"></div>
                        {specialty}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Performance Metrics */}
        <section className="mb-24 bg-[var(--bg-secondary)] -mx-6 px-6 py-16 rounded-2xl">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Analytics Implementation Results
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Measurable improvements achieved through professional analytics implementation and optimization.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { metric: "73%", label: "Faster Decision Making" },
              { metric: "45%", label: "Improved Attribution Accuracy" },
              { metric: "89%", label: "Reduction in Manual Reporting" },
              { metric: "2.4x", label: "ROI Improvement" }
            ].map((stat, index) => (
              <div key={stat.label} className={`text-center animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="h1 text-[var(--accent-primary)] mb-4">{stat.metric}</div>
                <div className="text-[var(--text-muted)]">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="feature-card">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Ready to Unlock Your Data's Potential?
            </h2>
            <p className="body-lg mb-12 max-w-3xl mx-auto">
              Transform your data chaos into clear, actionable insights with our comprehensive 
              analytics solutions. Start with a free analytics audit and discover what's possible.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">
                Get Free Analytics Audit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/ops/pricing" className="btn-secondary">
                Explore Analytics Packages
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AnalyticsPage;